<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div class="fw-bolder fs-6">Ultrassonografias</div>
      <button v-if="!readonly && !form" type="button" class="btn btn-sm btn-light-primary" @click="startCreate">
        <i class="bi bi-plus-lg me-1"></i> USG
      </button>
    </div>

    <div v-if="form" class="card card-bordered mb-4">
      <div class="card-body p-4">
        <div class="row g-3">
          <div class="col-md-4">
            <label class="form-label fs-7">Tipo</label>
            <select class="form-select form-select-sm" :class="{ 'is-invalid': errors.ultrasound_type }" v-model="form.ultrasound_type">
              <option v-for="option in typeOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
            <small class="text-danger" v-if="errors.ultrasound_type">{{ errors.ultrasound_type }}</small>
          </div>
          <div class="col-md-3">
            <label class="form-label fs-7">Data</label>
            <input type="date" class="form-control form-control-sm" :class="{ 'is-invalid': errors.ultrasound_date }" v-model="form.ultrasound_date" :max="today">
            <small class="text-danger" v-if="errors.ultrasound_date">{{ errors.ultrasound_date }}</small>
          </div>
          <div class="col-md-2" v-if="fetusCount > 1">
            <label class="form-label fs-7">Feto</label>
            <select class="form-select form-select-sm" :class="{ 'is-invalid': errors.fetus_number }" v-model.number="form.fetus_number">
              <option v-for="number in fetusCount" :key="number" :value="number">{{ fetusLabel(number) }}</option>
            </select>
            <small class="text-danger" v-if="errors.fetus_number">{{ errors.fetus_number }}</small>
          </div>
          <div class="col-md-3">
            <label class="form-label fs-7">IG pela biometria</label>
            <div class="input-group input-group-sm">
              <input type="number" min="4" max="44" class="form-control" :class="{ 'is-invalid': errors.biometry_weeks }" v-model="form.biometry_weeks" placeholder="sem">
              <span class="input-group-text">s</span>
              <input type="number" min="0" max="6" class="form-control" :class="{ 'is-invalid': errors.biometry_days }" v-model="form.biometry_days" placeholder="d">
              <span class="input-group-text">d</span>
            </div>
            <small class="text-danger" v-if="errors.biometry_weeks || errors.biometry_days">{{ errors.biometry_weeks || errors.biometry_days }}</small>
          </div>

          <div class="col-md-2 col-6" v-for="field in numericFields" :key="field.key">
            <label class="form-label fs-7">{{ field.label }}</label>
            <input type="text" inputmode="decimal" class="form-control form-control-sm" :class="{ 'is-invalid': errors[field.key] }" v-model="form[field.key]">
            <small class="text-danger" v-if="errors[field.key]">{{ errors[field.key] }}</small>
          </div>

          <div class="col-md-3">
            <label class="form-label fs-7">Placenta</label>
            <select class="form-select form-select-sm" v-model="form.placenta_location">
              <option value="">—</option>
              <option v-for="option in placentaOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label fs-7">Apresentação</label>
            <select class="form-select form-select-sm" v-model="form.presentation">
              <option value="">—</option>
              <option v-for="option in presentationOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
          <div class="col-md-3">
            <label class="form-label fs-7">Doppler</label>
            <select class="form-select form-select-sm" v-model="form.doppler">
              <option value="">—</option>
              <option v-for="option in dopplerOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
            </select>
          </div>
          <div class="col-12">
            <label class="form-label fs-7">Conclusão / observações</label>
            <textarea class="form-control form-control-sm" rows="2" v-model="form.notes"></textarea>
          </div>
        </div>
        <div class="d-flex justify-content-end gap-2 mt-3">
          <button type="button" class="btn btn-sm btn-light" @click="form = null">Cancelar</button>
          <button type="button" class="btn btn-sm btn-primary" :disabled="saving" @click="save">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span> Salvar
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-center py-6"><span class="spinner-border text-primary"></span></div>
    <div v-else-if="!ultrasounds.length" class="text-muted fs-7 text-center py-4">Nenhuma ultrassonografia registrada.</div>
    <div v-else class="d-flex flex-column gap-3">
      <div v-for="ultrasound in ultrasounds" :key="ultrasound.id" class="border rounded p-3">
        <div class="d-flex justify-content-between align-items-start gap-2">
          <div>
            <div class="fw-bolder fs-7">
              {{ ultrasound.ultrasound_type_label }}
              <span v-if="fetusCount > 1" class="badge badge-light-info ms-1">{{ ultrasound.fetus_label }}</span>
            </div>
            <div class="text-muted fs-8">{{ formatDateBr(ultrasound.ultrasound_date) }} · IG {{ ultrasound.gestational_age?.label }}</div>
          </div>
          <div class="text-nowrap" v-if="!readonly">
            <button type="button" class="btn btn-sm btn-icon btn-light" title="Editar" @click="startEdit(ultrasound)">
              <i class="bi bi-pencil"></i>
            </button>
            <button v-if="canDelete" type="button" class="btn btn-sm btn-icon btn-light-danger ms-1" title="Excluir" @click="remove(ultrasound)">
              <i class="bi bi-trash"></i>
            </button>
          </div>
        </div>
        <div class="d-flex flex-wrap gap-4 fs-7 mt-2">
          <span v-if="ultrasound.biometry_gestational_age">Biometria: <b>{{ ultrasound.biometry_gestational_age.label }}</b></span>
          <span v-if="ultrasound.efw_g">PFE: <b>{{ ultrasound.efw_g }} g</b><span v-if="ultrasound.efw_percentile !== null"> (p{{ formatNumber(ultrasound.efw_percentile) }})</span></span>
          <span v-if="ultrasound.afi_cm !== null">ILA: <b>{{ formatNumber(ultrasound.afi_cm, 'cm') }}</b></span>
          <span v-if="ultrasound.placenta_location_label">Placenta: <b>{{ ultrasound.placenta_location_label }}</b></span>
          <span v-if="ultrasound.presentation_label">Apresentação: <b>{{ ultrasound.presentation_label }}</b></span>
          <span v-if="ultrasound.fetal_heart_rate">BCF: <b>{{ ultrasound.fetal_heart_rate }} bpm</b></span>
          <span v-if="ultrasound.nuchal_translucency_mm !== null">TN: <b>{{ formatNumber(ultrasound.nuchal_translucency_mm, 'mm') }}</b></span>
          <span v-if="ultrasound.cervical_length_mm !== null">Colo: <b>{{ formatNumber(ultrasound.cervical_length_mm, 'mm') }}</b></span>
          <span v-if="ultrasound.doppler_label">Doppler: <b>{{ ultrasound.doppler_label }}</b></span>
        </div>
        <div v-if="ultrasound.notes" class="text-gray-700 fs-7 mt-2">{{ ultrasound.notes }}</div>
        <div v-for="alert in ultrasound.alerts" :key="alert.code" class="alert py-1 px-2 mt-2 mb-0 fs-8" :class="alertClass(alert.severity)">
          <i class="bi bi-exclamation-triangle me-1"></i>{{ alert.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import http from '../../../../helpers/http';
import Alert from '../../../../helpers/alert';
import {
  DOPPLER_OPTIONS,
  PLACENTA_OPTIONS,
  PRESENTATION_OPTIONS,
  ULTRASOUND_TYPE_OPTIONS,
  alertClass,
  buildPayload,
  fetusLabel,
  formatDateBr,
  formatNumber,
  todayIso,
  validationErrors,
} from '../prenatal';

const NUMERIC_FIELDS = [
  { key: 'efw_g', label: 'PFE (g)' },
  { key: 'efw_percentile', label: 'Percentil PFE' },
  { key: 'afi_cm', label: 'ILA (cm)' },
  { key: 'fetal_heart_rate', label: 'BCF (bpm)' },
  { key: 'nuchal_translucency_mm', label: 'TN (mm)' },
  { key: 'cervical_length_mm', label: 'Colo (mm)' },
];

const EDITABLE_FIELDS = [
  'ultrasound_type', 'ultrasound_date', 'fetus_number', 'biometry_weeks', 'biometry_days',
  ...NUMERIC_FIELDS.map((field) => field.key),
  'placenta_location', 'presentation', 'doppler', 'notes',
];

export default {
  name: 'PrenatalUltrasoundsTab',
  emits: ['changed'],
  props: {
    baseUrl: { type: String, required: true },
    fetusCount: { type: Number, default: 1 },
    readonly: Boolean,
    canDelete: Boolean,
  },
  data() {
    return {
      loading: false,
      saving: false,
      ultrasounds: [],
      form: null,
      editingId: null,
      errors: {},
      today: todayIso(),
      numericFields: NUMERIC_FIELDS,
      typeOptions: ULTRASOUND_TYPE_OPTIONS,
      placentaOptions: PLACENTA_OPTIONS,
      presentationOptions: PRESENTATION_OPTIONS,
      dopplerOptions: DOPPLER_OPTIONS,
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    alertClass,
    fetusLabel,
    formatDateBr,
    formatNumber,
    async load() {
      this.loading = true;
      try {
        const response = await http.get(`${this.baseUrl}/ultrasounds`);
        this.ultrasounds = [...response.data].reverse();
      } catch (_) {
        Alert.toastError('Falha ao carregar as ultrassonografias.');
      } finally {
        this.loading = false;
      }
    },
    startCreate() {
      this.editingId = null;
      this.errors = {};
      this.form = Object.fromEntries(EDITABLE_FIELDS.map((field) => [field, '']));
      this.form.ultrasound_type = 'obstetric';
      this.form.ultrasound_date = this.today;
      this.form.fetus_number = 1;
    },
    startEdit(ultrasound) {
      this.editingId = ultrasound.id;
      this.errors = {};
      this.form = Object.fromEntries(EDITABLE_FIELDS.map((field) => [field, ultrasound[field] ?? '']));
    },
    async save() {
      this.saving = true;
      this.errors = {};
      const payload = buildPayload(this.form, ['fetus_number', 'biometry_weeks', 'biometry_days', ...NUMERIC_FIELDS.map((field) => field.key)]);

      try {
        if (this.editingId) {
          await http.put(`${this.baseUrl}/ultrasounds/${this.editingId}`, payload);
        } else {
          await http.post(`${this.baseUrl}/ultrasounds`, payload);
        }
        this.form = null;
        Alert.toastSuccess('Ultrassonografia salva.');
        await this.load();
        this.$emit('changed');
      } catch (error) {
        this.errors = validationErrors(error);
        if (!Object.keys(this.errors).length) {
          Alert.toastError(error.response?.data?.message || 'Falha ao salvar a ultrassonografia.');
        }
      } finally {
        this.saving = false;
      }
    },
    async remove(ultrasound) {
      const result = await Swal.fire({
        title: 'Excluir ultrassonografia?',
        text: `${ultrasound.ultrasound_type_label} de ${formatDateBr(ultrasound.ultrasound_date)}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Excluir',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-secondary' },
      });
      if (!result.isConfirmed) {
        return;
      }

      try {
        await http.delete(`${this.baseUrl}/ultrasounds/${ultrasound.id}`);
        await this.load();
        this.$emit('changed');
      } catch (error) {
        Alert.toastError(error.response?.data?.message || 'Falha ao excluir a ultrassonografia.');
      }
    },
  },
};
</script>
